import React from 'react'
import { Link } from 'react-router-dom'
import Navigation from '../components/Navigation'
import Footer from '../components/Footer'

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="container-padding py-12 max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-sm text-gray-600 mb-8">Last Updated: December 28, 2025</p>

          <div className="prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                Rigel Labs LLC ("we", "our", or "us") operates FlowBeatIQ, a privacy-compliant foot traffic analytics
                platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information
                when you use our Service.
              </p>
              <p className="text-gray-700 mb-4">
                We are committed to protecting privacy - both yours as a customer and the privacy of visitors to your
                business locations. This policy describes our privacy practices in detail.
              </p>
              <p className="text-gray-700">
                By using FlowBeatIQ, you agree to the collection and use of information in accordance with this policy.
                If you do not agree with our policies and practices, do not use our Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.1 Information You Provide to Us</h3>
              <p className="text-gray-700 mb-4">
                When you create an account or use our Service, we collect information that you provide directly:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Account Information:</strong> Business name, your name, email address, password (encrypted), and phone number</li>
                <li><strong>Business Information:</strong> Business location addresses, operating hours, and industry type</li>
                <li><strong>Payment Information:</strong> Billing address and payment method details (processed securely by Stripe; we do not store full credit card numbers)</li>
                <li><strong>Communications:</strong> Information you provide when contacting customer support or communicating with us</li>
                <li><strong>Preferences:</strong> Dashboard settings, notification preferences, and other customization choices</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.2 Visitor Data Collected by Sensors</h3>
              <p className="text-gray-700 mb-4">
                Our WiFi sensors deployed at your business locations collect anonymized visitor data:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>WiFi Probe Requests:</strong> Anonymized WiFi probe requests from mobile devices (smartphones, tablets, etc.)</li>
                <li><strong>MAC Addresses (Hashed):</strong> Device MAC addresses are immediately hashed using one-way cryptographic hashing, making them non-reversible and anonymous</li>
                <li><strong>Signal Strength (RSSI):</strong> WiFi signal strength used to estimate proximity and dwell time</li>
                <li><strong>Timestamps:</strong> Date and time of detection for analytics purposes</li>
                <li><strong>Sensor Location:</strong> Which sensor detected the device (for multi-location tracking)</li>
              </ul>
              <p className="text-gray-700 mb-4">
                <strong className="text-blue-600">Important Privacy Note:</strong> We do NOT collect:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Names, email addresses, or phone numbers of visitors</li>
                <li>Internet browsing history or website visits</li>
                <li>WiFi network passwords or credentials</li>
                <li>The contents of network communications</li>
                <li>Any personally identifiable information (PII) about visitors</li>
              </ul>

              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-4">
                <p className="text-gray-700">
                  <strong>Important Clarification on Anonymization:</strong> Hashed device identifiers are processed
                  using one-way cryptographic hashing with rotating salts and are not retained in a form that allows
                  re-identification of individuals or long-term tracking across locations or time.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.3 Automatically Collected Information</h3>
              <p className="text-gray-700 mb-4">
                When you use our Service, we automatically collect certain information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Usage Data:</strong> Pages viewed, features used, time spent in the dashboard, and interactions with our Service</li>
                <li><strong>Device Information:</strong> Browser type, operating system, IP address, and device identifiers</li>
                <li><strong>Sensor Telemetry:</strong> Sensor health status, uptime, firmware version, and performance metrics</li>
                <li><strong>Log Data:</strong> Access times, error logs, and diagnostic information</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.4 Third-Party Integrations</h3>
              <p className="text-gray-700 mb-4">
                We integrate with third-party services that may collect additional information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Weather Data:</strong> We use Open-Meteo API to correlate foot traffic with weather conditions (no personal data shared)</li>
                <li><strong>Payment Processing:</strong> Stripe processes payment information according to their privacy policy</li>
                <li><strong>Email Services:</strong> Email service providers process email communications (support, notifications, etc.)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.5 Data Controller and Data Processor Roles</h3>
              <p className="text-gray-700 mb-4">
                For visitor analytics data collected via FlowBeatIQ sensors, the business customer is the <strong>Data Controller</strong> and
                FlowBeatIQ acts as a <strong>Data Processor</strong> on the customer's behalf.
              </p>
              <p className="text-gray-700 mb-4">
                FlowBeatIQ processes visitor analytics data solely according to the documented instructions of the business customer and
                in accordance with this Privacy Policy and applicable data protection laws.
              </p>
              <p className="text-gray-700">
                Where FlowBeatIQ acts as a data processor, processing is governed by our{' '}
                <Link to="/dpa" className="text-blue-600 hover:text-blue-700">Data Processing Addendum (DPA)</Link>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">
                We use the collected information for the following purposes:
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.1 Provide and Improve the Service</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Process visitor analytics and generate foot traffic insights</li>
                <li>Create dashboards, reports, and visualizations</li>
                <li>Detect anomalies and send alerts</li>
                <li>Provide AI-powered insights and recommendations</li>
                <li>Monitor and improve sensor performance</li>
                <li>Develop new features and enhance existing functionality</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.2 Account Management and Support</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Create and manage your account</li>
                <li>Process payments and billing</li>
                <li>Respond to customer support inquiries</li>
                <li>Send service-related notifications and updates</li>
                <li>Verify your identity and prevent fraud</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.3 Communications</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Send email notifications about sensor status and alerts</li>
                <li>Provide important service updates and announcements</li>
                <li>Send marketing communications (you can opt-out anytime)</li>
                <li>Request feedback and conduct surveys</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.4 Legal and Security</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Comply with legal obligations and enforce our Terms of Service</li>
                <li>Protect against fraud, abuse, and security threats</li>
                <li>Resolve disputes and troubleshoot problems</li>
                <li>Conduct internal audits and data analysis</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. How We Share Your Information</h2>
              <p className="text-gray-700 mb-4">
                We do not sell, rent, or trade your personal information. We may share information in the following circumstances:
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.1 Service Providers</h3>
              <p className="text-gray-700 mb-4">
                We share information with trusted third-party service providers who assist us in operating the Service:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Payment Processors:</strong> Stripe (for billing and subscription management)</li>
                <li><strong>Email Services:</strong> Email delivery providers (for transactional and notification emails)</li>
                <li><strong>Cloud Hosting:</strong> Infrastructure providers (for data storage and processing)</li>
                <li><strong>Analytics Tools:</strong> Service analytics and monitoring platforms</li>
              </ul>
              <p className="text-gray-700 mb-4">
                These providers are contractually obligated to protect your information and use it only for the purposes we specify.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.2 Business Transfers</h3>
              <p className="text-gray-700 mb-4">
                If we are involved in a merger, acquisition, or sale of assets, your information may be transferred.
                We will provide notice before your information becomes subject to a different privacy policy.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.3 Legal Requirements</h3>
              <p className="text-gray-700 mb-4">
                We may disclose information if required to do so by law or in response to valid requests by public
                authorities (e.g., court orders, subpoenas, government requests).
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.4 Protection of Rights</h3>
              <p className="text-gray-700 mb-4">
                We may disclose information to protect our rights, property, or safety, or the rights, property,
                or safety of our users or others.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.5 With Your Consent</h3>
              <p className="text-gray-700 mb-4">
                We may share information with third parties when you give us explicit consent to do so.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Retention</h2>
              <p className="text-gray-700 mb-4">
                We retain your information for as long as necessary to provide the Service and fulfill the purposes
                outlined in this Privacy Policy:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Account Data:</strong> Retained while your account is active and for a reasonable period after account closure (typically 90 days) unless required for legal or business purposes</li>
                <li><strong>Analytics Data:</strong> Visitor analytics data is retained according to your subscription plan (typically 12-24 months for historical analysis)</li>
                <li><strong>Billing Records:</strong> Retained for 7 years to comply with tax and financial regulations</li>
                <li><strong>Sensor Logs:</strong> Technical logs are retained for 30-90 days for troubleshooting and performance monitoring</li>
              </ul>
              <p className="text-gray-700">
                You may request deletion of your data at any time by contacting us at support@flowbeatiq.com.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Security</h2>
              <p className="text-gray-700 mb-4">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Encryption:</strong> All data in transit is encrypted using TLS/SSL. Sensitive data at rest is encrypted using AES-256</li>
                <li><strong>Authentication:</strong> Passwords are hashed using bcrypt. Multi-factor authentication available for added security</li>
                <li><strong>Access Controls:</strong> Role-based access controls limit data access to authorized personnel only</li>
                <li><strong>Monitoring:</strong> Continuous security monitoring and logging to detect and respond to threats</li>
                <li><strong>Regular Audits:</strong> Periodic security assessments and vulnerability testing</li>
                <li><strong>Secure Infrastructure:</strong> Data hosted on secure, compliant cloud infrastructure</li>
              </ul>
              <p className="text-gray-700">
                However, no method of transmission over the Internet or electronic storage is 100% secure. While we
                strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Privacy Rights</h2>
              <p className="text-gray-700 mb-4">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">7.1 Access and Portability</h3>
              <p className="text-gray-700 mb-4">
                You have the right to request a copy of the personal information we hold about you. You can export
                your analytics data directly from the dashboard.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">7.2 Correction</h3>
              <p className="text-gray-700 mb-4">
                You can update your account information and business details directly in your account settings.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">7.3 Deletion</h3>
              <p className="text-gray-700 mb-4">
                You can request deletion of your account and personal information by contacting support@flowbeatiq.com.
                We will delete your data within 30 days, except where retention is required by law.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">7.4 Opt-Out of Marketing</h3>
              <p className="text-gray-700 mb-4">
                You can opt-out of marketing emails by clicking the "unsubscribe" link in any marketing email or
                by adjusting your notification preferences in account settings.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">7.5 California Residents (CCPA)</h3>
              <p className="text-gray-700 mb-4">
                If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Right to know what personal information we collect, use, and disclose</li>
                <li>Right to request deletion of your personal information</li>
                <li>Right to opt-out of the "sale" of personal information (note: we do not sell personal information)</li>
                <li>Right to non-discrimination for exercising your privacy rights</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">7.6 European Residents (GDPR)</h3>
              <p className="text-gray-700 mb-4">
                If you are located in the European Economic Area (EEA), you have rights under the General Data Protection Regulation (GDPR):
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Right to access your personal data</li>
                <li>Right to rectification of inaccurate data</li>
                <li>Right to erasure ("right to be forgotten")</li>
                <li>Right to restrict processing</li>
                <li>Right to data portability</li>
                <li>Right to object to processing</li>
                <li>Right to withdraw consent at any time</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">7.7 Exercising Your Rights</h3>
              <p className="text-gray-700 mb-4">
                To exercise any of these rights, please contact us at support@flowbeatiq.com. We will respond to
                your request within 30 days. We may ask you to verify your identity before processing your request.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 mb-4">
                We use cookies and similar tracking technologies to enhance your experience:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Essential Cookies:</strong> Required for authentication and basic functionality (cannot be disabled)</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how you use the Service to improve it</li>
                <li><strong>Session Storage:</strong> Temporary storage for maintaining your session and preferences</li>
              </ul>
              <p className="text-gray-700">
                You can control cookies through your browser settings. Disabling certain cookies may limit functionality.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
              <p className="text-gray-700">
                Our Service is not intended for children under 13 years of age. We do not knowingly collect personal
                information from children. If you become aware that a child has provided us with personal information,
                please contact us, and we will take steps to delete such information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. International Data Transfers</h2>
              <p className="text-gray-700 mb-4">
                Your information may be transferred to and maintained on servers located outside your state, province,
                country, or other governmental jurisdiction where data protection laws may differ.
              </p>
              <p className="text-gray-700 mb-4">
                If you are located outside the United States and choose to provide information to us, we transfer
                personal information to the United States and process it there. By using our Service, you consent
                to this transfer.
              </p>
              <p className="text-gray-700">
                Where required by applicable law, international data transfers are conducted pursuant to Standard
                Contractual Clauses (SCCs) or other legally recognized transfer safeguards.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Third-Party Links and Services</h2>
              <p className="text-gray-700">
                Our Service may contain links to third-party websites or services that are not operated by us.
                We are not responsible for the privacy practices of these third parties. We encourage you to
                review their privacy policies before providing any information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of material changes by:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Sending an email to the address associated with your account</li>
                <li>Posting a notice on our Service or website</li>
                <li>Updating the "Last Updated" date at the top of this policy</li>
              </ul>
              <p className="text-gray-700">
                Your continued use of the Service after changes become effective constitutes acceptance of the
                revised Privacy Policy. We encourage you to review this policy periodically.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Privacy for Business Visitors</h2>
              <p className="text-gray-700 mb-4">
                <strong>For visitors to businesses using FlowBeatIQ sensors:</strong>
              </p>
              <p className="text-gray-700 mb-4">
                If you visit a business location that uses our WiFi sensors, here's what you should know:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Our sensors detect WiFi-enabled devices but do NOT collect personal information about you</li>
                <li>MAC addresses are immediately hashed and anonymized - we cannot identify you personally</li>
                <li>We do NOT track your location outside the business premises</li>
                <li>We do NOT access your browsing history, messages, or any data on your device</li>
                <li>We do NOT connect to or access your WiFi networks</li>
                <li>The data is used solely for foot traffic analytics to help businesses understand visitor patterns</li>
              </ul>
              <p className="text-gray-700 mb-4">
                You can disable WiFi on your device to prevent detection, though this will not affect your ability
                to visit the business.
              </p>

              <div className="bg-yellow-50 border-l-4 border-yellow-600 p-4">
                <p className="text-gray-700">
                  <strong>Notice Obligation for Businesses:</strong> Businesses using FlowBeatIQ are responsible for
                  providing appropriate notice to visitors regarding the use of WiFi-based analytics at their locations,
                  including signage or disclosures where required by applicable law.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-md">
                <p className="text-gray-700"><strong>Rigel Labs LLC - Privacy Team</strong></p>
                <p className="text-gray-700">Email: support@flowbeatiq.com</p>
                <p className="text-gray-700">Subject Line: "Privacy Inquiry"</p>
                <p className="text-gray-700">Website: https://flowbeatiq.com</p>
              </div>
              <p className="text-gray-700 mt-4">
                We will respond to your inquiry within 30 days.
              </p>
            </section>

            <div className="border-t border-gray-200 pt-8 mt-8">
              <p className="text-sm text-gray-600 mb-4">
                By using FlowBeatIQ, you acknowledge that you have read and understood this Privacy Policy and
                agree to its terms.
              </p>
              <Link
                to="/"
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors duration-200"
              >
                Return to Home
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default PrivacyPolicy
